// src/components/common/Button.jsx
export const Button = ({ 
  children, 
  variant = "primary", 
  size = "md", 
  fullWidth = false,
  disabled = false,
  onClick,
  type = "button",
  icon,
  ...props 
}) => {
  const baseStyles = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontWeight: "600",
    borderRadius: "8px",
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    fontFamily: "inherit",
    opacity: disabled ? 0.6 : 1,
    width: fullWidth ? "100%" : "auto"
  };

  const variants = {
    primary: {
      backgroundColor: "#00a680",
      color: "white",
      boxShadow: "0 2px 8px rgba(0, 166, 128, 0.3)"
    },
    secondary: {
      backgroundColor: "#f1f5f9",
      color: "#334155",
      border: "1px solid #e2e8f0"
    },
    danger: {
      backgroundColor: "#ef4444",
      color: "white",
      boxShadow: "0 2px 8px rgba(239, 68, 68, 0.3)"
    },
    ghost: {
      backgroundColor: "transparent",
      color: "#00a680",
      border: "1px solid #00a680"
    }
  };

  const sizes = {
    sm: { padding: "8px 16px", fontSize: "14px" },
    md: { padding: "12px 24px", fontSize: "16px" },
    lg: { padding: "16px 32px", fontSize: "18px" }
  };

  const hoverStyles = !disabled && {
    primary: { backgroundColor: "#059669", transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(0, 166, 128, 0.4)" },
    secondary: { backgroundColor: "#e2e8f0" },
    danger: { backgroundColor: "#dc2626", transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(239, 68, 68, 0.4)" },
    ghost: { backgroundColor: "#f0fdf4" }
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        ...baseStyles,
        ...variants[variant],
        ...sizes[size]
      }}
      onMouseEnter={(e) => {
        if (!disabled && hoverStyles[variant]) {
          Object.assign(e.target.style, hoverStyles[variant]);
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          Object.assign(e.target.style, variants[variant]);
          e.target.style.transform = "translateY(0)";
        }
      }}
      {...props}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
};

// src/components/common/Input.jsx
export const Input = ({ 
  label, 
  error, 
  icon,
  fullWidth = true,
  ...props 
}) => {
  const containerStyle = {
    marginBottom: "20px",
    width: fullWidth ? "100%" : "auto"
  };

  const labelStyle = {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "#334155",
    marginBottom: "8px"
  };

  const inputWrapperStyle = {
    position: "relative",
    display: "flex",
    alignItems: "center"
  };

  const iconStyle = {
    position: "absolute",
    left: "12px",
    color: "#94a3b8",
    fontSize: "18px"
  };

  const inputStyle = {
    width: "100%",
    padding: icon ? "12px 12px 12px 40px" : "12px 16px",
    fontSize: "16px",
    border: `2px solid ${error ? "#ef4444" : "#e2e8f0"}`,
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.2s ease",
    backgroundColor: "#ffffff",
    fontFamily: "inherit",
    boxSizing: "border-box"
  };

  const errorStyle = {
    marginTop: "6px",
    fontSize: "14px",
    color: "#ef4444"
  };

  return (
    <div style={containerStyle}>
      {label && <label style={labelStyle}>{label}</label>}
      <div style={inputWrapperStyle}>
        {icon && <span style={iconStyle}>{icon}</span>}
        <input
          style={inputStyle}
          onFocus={(e) => {
            e.target.style.borderColor = error ? "#ef4444" : "#00a680";
            e.target.style.boxShadow = `0 0 0 3px ${error ? "rgba(239, 68, 68, 0.1)" : "rgba(0, 166, 128, 0.1)"}`;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = error ? "#ef4444" : "#e2e8f0";
            e.target.style.boxShadow = "none";
          }}
          {...props}
        />
      </div>
      {error && <p style={errorStyle}>{error}</p>}
    </div>
  );
};

// src/components/common/Card.jsx
export const Card = ({ 
  children, 
  hover = false,
  padding = "24px",
  ...props 
}) => {
  const baseStyle = {
    backgroundColor: "white",
    borderRadius: "12px",
    padding,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s ease"
  };

  const hoverStyle = hover ? {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 24px rgba(0, 0, 0, 0.1)"
  } : {};

  return (
    <div
      style={baseStyle}
      onMouseEnter={(e) => {
        if (hover) Object.assign(e.currentTarget.style, hoverStyle);
      }}
      onMouseLeave={(e) => {
        if (hover) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.05)";
        }
      }}
      {...props}
    >
      {children}
    </div>
  );
};

// src/components/common/Badge.jsx
export const Badge = ({ children, variant = "default" }) => {
  const variants = {
    default: { backgroundColor: "#f1f5f9", color: "#475569" },
    success: { backgroundColor: "#dcfce7", color: "#166534" },
    warning: { backgroundColor: "#fef3c7", color: "#92400e" },
    danger: { backgroundColor: "#fee2e2", color: "#991b1b" },
    info: { backgroundColor: "#dbeafe", color: "#1e40af" }
  };

  const style = {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 12px",
    borderRadius: "16px",
    fontSize: "13px",
    fontWeight: "600",
    ...variants[variant]
  };

  return <span style={style}>{children}</span>;
};

// src/components/common/Select.jsx
export const Select = ({ 
  label, 
  options = [], 
  error,
  fullWidth = true,
  ...props 
}) => {
  const containerStyle = {
    marginBottom: "20px",
    width: fullWidth ? "100%" : "auto"
  };

  const labelStyle = {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "#334155",
    marginBottom: "8px"
  };

  const selectStyle = {
    width: "100%",
    padding: "12px 16px",
    fontSize: "16px",
    border: `2px solid ${error ? "#ef4444" : "#e2e8f0"}`,
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.2s ease",
    backgroundColor: "#ffffff",
    cursor: "pointer",
    fontFamily: "inherit",
    boxSizing: "border-box"
  };

  return (
    <div style={containerStyle}>
      {label && <label style={labelStyle}>{label}</label>}
      <select
        style={selectStyle}
        onFocus={(e) => {
          e.target.style.borderColor = error ? "#ef4444" : "#00a680";
          e.target.style.boxShadow = `0 0 0 3px ${error ? "rgba(239, 68, 68, 0.1)" : "rgba(0, 166, 128, 0.1)"}`;
        }}
        onBlur={(e) => {
          e.target.style.borderColor = error ? "#ef4444" : "#e2e8f0";
          e.target.style.boxShadow = "none";
        }}
        {...props}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <p style={{ marginTop: "6px", fontSize: "14px", color: "#ef4444" }}>{error}</p>}
    </div>
  );
};

// src/components/common/Alert.jsx
export const Alert = ({ children, variant = "info", onClose }) => {
  const variants = {
    success: { backgroundColor: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" },
    error: { backgroundColor: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" },
    warning: { backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" },
    info: { backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #bfdbfe" }
  };

  const style = {
    padding: "16px 20px",
    borderRadius: "8px",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...variants[variant]
  };

  return (
    <div style={style}>
      <span>{children}</span>
      {onClose && (
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: "20px",
            color: "inherit",
            opacity: 0.7
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

// src/components/common/Loading.jsx
export const Loading = ({ text = "Cargando..." }) => (
  <div style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px",
    gap: "16px"
  }}>
    <div style={{
      width: "40px",
      height: "40px",
      border: "4px solid #e2e8f0",
      borderTop: "4px solid #00a680",
      borderRadius: "50%",
      animation: "spin 1s linear infinite"
    }} />
    <p style={{ color: "#64748b", fontSize: "16px" }}>{text}</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);